import React from 'react';
import { MISSING_TARIFF_LABEL, DELETED_TARIFF_LABEL } from '../../../utils/fscTariff';
import type { FscTariff } from '../../../services/quote.service';
import FscTariffLookupModal from './FscTariffLookupModal';

/**
 * The "Cust Tariff #" value on a CUSTOMER FSC lane: the tariff number, the two
 * broken states, and the Lookup that repoints it.
 *
 * Shared by the Direct tab and the Dray tab. The two lanes store their FSC in
 * different columns -- directquote.fsctype/fsctariff/fscrate against
 * transloadquote.fueltype/fueltariff/fuelsurcharge -- so the pick comes back
 * through `onPick` and each tab writes its own three fields. Everything the
 * CSR sees is here, which is the point: a warehouse quote was showing a bare
 * number input with no lookup, no "Missing Tariff" and no dead-link mark.
 */
interface Props {
  /** The tariff number on the lane right now (draft value, not the saved one). */
  tariffNumber: string | number | null | undefined;
  /** The quote's bill-to recno. Without one there is nothing to look up. */
  billTo?: number;
  /**
   * The SAVED tariff number when that tariff has been deleted from
   * fueldata.fsc_tariff. Passing the NUMBER rather than a bare flag keeps the
   * mark tied to the saved tariff, so a replacement picked in this session
   * renders normally without waiting for a reload.
   */
  deletedTariffNumber?: number | null;
  /** The tab is editable, so a pick writes through `onPick`. */
  editable?: boolean;
  /**
   * Secured-quote exemption: assign a still-broken tariff even though the tab
   * is read-only. Hosts pass this only when the saved quote is secured and its
   * tariff is missing or deleted; once a live tariff exists it re-locks.
   */
  onAssignTariff?: (t: FscTariff) => void;
  /** Where an editable tab's pick goes. */
  onPick?: (t: FscTariff) => void;
}

const CustTariffValue: React.FC<Props> = ({
  tariffNumber, billTo, deletedTariffNumber, editable = false, onAssignTariff, onPick,
}) => {
  const [lookupOpen, setLookupOpen] = React.useState(false);

  // Renders as a link into the native Fuel Surcharge Tariff maintenance page
  // (new tab, pre-filtered to this tariff). Null when there is no tariff yet.
  const tariffNum = Number(tariffNumber) || 0;
  const tariffLink = tariffNum > 0 ? (
    <a
      href={`/data-management/fuel-surcharge-tariff?q=${tariffNum}`}
      target="_blank"
      rel="noopener noreferrer"
      title="Open this tariff in Fuel Surcharge Tariff maintenance"
      // px-1.5 matches FieldValue / the inputs so the tariff # lines up with the
      // FSC value and the Fuel Type control above it.
      className="px-1.5 text-[0.78125rem] font-semibold text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
    >
      {tariffNum}
    </a>
  ) : null;

  // The saved tariff is deleted from fueldata (every revision deleted='Y'), so
  // the link is dead -- nothing resolves and the invoice charges no FSC. Only
  // marks the SAVED number: a replacement picked here renders normally.
  const tariffDeleted = tariffNum > 0 && tariffNum === (Number(deletedTariffNumber) || 0);
  // Whether this host lets the tariff be re-picked at all (an editable tab, or a
  // secured quote exempted to fix a broken link) -- and there is a bill-to to
  // look tariffs up for. Drives whether the struck-through number is clickable.
  const pickable = !!(editable || onAssignTariff) && (billTo ?? 0) > 0;
  const deletedStyle = 'px-1.5 text-[0.78125rem] font-semibold line-through text-red-600 dark:text-red-400';
  const deletedTitle = pickable
    ? 'This fuel tariff has been deleted — click to pick a new one'
    : 'This fuel tariff has been deleted';
  const deletedMark = (
    <>
      {pickable ? (
        <button
          type="button"
          onClick={() => setLookupOpen(true)}
          title={deletedTitle}
          className={`${deletedStyle} hover:text-red-700 dark:hover:text-red-300`}
        >
          {tariffNum}
        </button>
      ) : (
        <span title={deletedTitle} className={deletedStyle}>{tariffNum}</span>
      )}
      <span className="text-[0.71875rem] font-semibold text-red-600 dark:text-red-400">
        {DELETED_TARIFF_LABEL}
      </span>
    </>
  );

  // CUSTOMER FSC with no tariff number (e.g. a migrated quote whose customer
  // tariff isn't in v1 yet -- the migration no longer auto-creates it). Shown in
  // place of the tariff link so the gap is obvious and a CSR can assign one.
  const missingBadge = (
    <span
      title="This customer's fuel-surcharge tariff isn't set up yet"
      className="px-1.5 text-[0.78125rem] font-semibold text-red-600 dark:text-red-400"
    >
      {MISSING_TARIFF_LABEL}
    </span>
  );

  const mark = tariffDeleted ? deletedMark : (tariffLink ?? missingBadge);

  return (
    <>
      {/* Lookup shows when the tab is editable OR when a secured quote is
          exempted to assign a still-broken tariff; otherwise it is read-only. */}
      {(editable || onAssignTariff) ? (
        <>
          {mark}
          <button
            type="button"
            onClick={() => setLookupOpen(true)}
            disabled={!((billTo ?? 0) > 0)}
            title={(billTo ?? 0) > 0 ? 'Look up a customer FSC tariff' : 'No bill-to on this quote'}
            className="text-[0.71875rem] font-semibold text-blue-600 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-default dark:text-blue-400 dark:hover:text-blue-300 dark:disabled:text-slate-600"
          >
            Lookup
          </button>
        </>
      ) : mark}
      <FscTariffLookupModal
        open={lookupOpen}
        billto={billTo ?? 0}
        onClose={() => setLookupOpen(false)}
        onPick={(t) => {
          // Editable tab -> the tab writes its own three FSC fields. Secured
          // broken-tariff exemption (not editable) -> the host stages just the
          // tariff assignment.
          if (editable) onPick?.(t);
          else onAssignTariff?.(t);
        }}
      />
    </>
  );
};

export default CustTariffValue;
